import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Planting } from '../../../../../models/Planting';
import type { LockedLocationIds, LockedLocationInfo } from '../types';
import { normalizeId } from '../utils';

/**
 * Determines locked location when opening the form from a bed page
 * (single default location with a known bed).
 */
export const useLockedLocation = (
  defaultLocations?: Planting['locations'],
): {
  lockedLocation: LockedLocationIds | null;
  lockedLocationInfo: LockedLocationInfo | null;
} => {
  const { t } = useTranslation('plantings');
  const lockedLocation = useMemo<LockedLocationIds | null>(() => {
    if (!defaultLocations || defaultLocations.length !== 1) return null;
    const location = defaultLocations[0];
    const bedId = location.bed?.id ?? location.bedId ?? null;
    const plotId = location.plot?.id ?? location.bed?.plot_id ?? null;
    const fieldId = location.field?.id ?? location.plot?.field_id ?? null;
    if (!bedId) return null;
    return {
      fieldId: normalizeId(fieldId),
      plotId: normalizeId(plotId),
      bedId: normalizeId(bedId),
    };
  }, [defaultLocations]);

  const lockedLocationInfo = useMemo<LockedLocationInfo | null>(() => {
    if (!lockedLocation || !defaultLocations || defaultLocations.length === 0) return null;
    const location = defaultLocations[0];
    const fieldLabel = location.field?.name
      ? location.field.name
      : lockedLocation.fieldId != null
        ? t('location.fieldNamed', { name: `#${lockedLocation.fieldId}` })
        : t('form.location.fieldUnknown');
    const plotLabel = location.plot?.name
      ? location.plot.name
      : lockedLocation.plotId != null
        ? t('location.plotNamed', { name: `#${lockedLocation.plotId}` })
        : t('form.location.plotUnknown');
    const bedLabel = location.bed?.anchor_code
      ? t('location.bedNamed', { name: location.bed.anchor_code })
      : lockedLocation.bedId != null
        ? t('location.bedNamed', { name: `#${lockedLocation.bedId}` })
        : t('form.location.bedUnknown');
    return { fieldLabel, plotLabel, bedLabel };
  }, [defaultLocations, lockedLocation, t]);

  return { lockedLocation, lockedLocationInfo };
};
